import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSiteContent } from '../../contexts/SiteContentContext';
import { FadeLink, Markup, Section, cx, reducedMotion, useCopy } from '../core';
import ProductFamily from '../ProductCards';
import heroIso from '../svg/heroIso';
import workflow1 from '../svg/workflow1';
import workflow2 from '../svg/workflow2';
import workflow3 from '../svg/workflow3';
import partnerAcademic from '../svg/partnerAcademic';
import partnerIndustry from '../svg/partnerIndustry';
import partnerInvestment from '../svg/partnerInvestment';

const HOME_SECTION = { threshold: 0.22, rootMargin: '0px' };

const copy = {
  en: {
    proof: 'Enterprise Trustworthy AI Agents',
    title: 'AI agents for real work, with clear boundaries.',
    sub: 'AI workspaces for people and organizations. Built with context, permissions and human review.',
    partner: 'Partner with us', explore: 'Explore products',
    facts: [['3', 'AI products'], ['4-stage', 'Engagement process'], ['Human', 'Review before delivery']],
    stage: 'Concept illustration: Need, Plan, Sources, Tasks and roles, Human review',
    legend: ['INPUT', 'COORDINATE', 'HUMAN REVIEW'],
    filmLabel: 'Meet AgentecFusion', filmTitle: <>From AI research to<br />real-world products.</>,
    filmNote: 'A short look at who we are, what we build, and how we work with organizations.',
    filmName: 'AgentecFusion · Brand film', filmHint: 'Loads only on click', play: 'Play brand film (loads only on click)',
    filmPoster: 'Brand film poster', filmLoading: 'Loading video player…', filmError: 'The video could not be loaded. Please use the backup link.',
    backup: 'Watch on backup link', transcript: 'Read transcript', filmSoon: 'Brand film coming soon',
    flowPill: 'An illustrative workflow', flowTitle: 'From a need to a human decision.',
    flowNote: 'A concept illustration. No backend connection or live task execution.', flowSteps: 'Workflow steps', step: 'STEP',
    steps: [
      ['INPUT', 't-in', 'Start with a need and its context', 'Frame the task, available sources and permission boundaries.'],
      ['COORDINATE', 't-co', 'Bring roles and tasks together', 'Illustrate how planning, research and execution relate.'],
      ['REVIEW', 't-re', 'Keep people at the decision point', 'Check the evidence, review the work and decide what comes next.']
    ],
    solEyebrow: 'Custom solutions', solTitle: <>Built around<br />your organization.</>,
    solSub: 'Discuss AI for your organization around a specific use case, existing systems and data boundaries.',
    solButton: 'Explore solutions', principles: ['Scoped with you', 'Your data stays bounded', 'Reviewed at every step'],
    solRows: [
      ['Frame the need', 'Use case & goals', 'We start from one concrete workflow — who uses it, what should change, and how success is judged.', ['Owner', 'Users', 'Success criteria']],
      ['Explore the fit', 'Systems & boundaries', 'We map the systems, data and permissions involved, and agree what an agent may and may not touch.', ['Systems', 'Data', 'Permissions']],
      ['Plan validation', 'Review & acceptance', 'We define how results are reviewed, who signs off, and what counts as accepted before anything goes live.', ['Reviewers', 'Criteria', 'Sign-off']]
    ],
    partEyebrow: 'Partnerships', partTitle: 'Better ideas, built together.', partLink: 'Explore Partnerships',
    paths: [
      ['research', 'Academic research', 'Universities & research labs', 'Joint research, pilots of Learn in teaching settings, and student projects.', 'Enquire as academic partner'],
      ['industry', 'Product partnership', 'Companies & institutions', 'Co-design agent workflows on your own data, with clear review points.', 'Enquire as industry partner'],
      ['investment', 'Strategic & investment', 'Strategic investors', 'Conversations about where AgentecFusion is heading and how to grow together.', 'Enquire about investment']
    ],
    ctaTitle: <>Have a problem worth<br />building for?</>,
    ctaText: 'Tell us about your organization and what you are exploring. We will route it to the right person.',
    ctaTalk: 'Talk to the team', ctaExplore: 'Explore products'
  },
  zh: {
    proof: '企业级可信 AI 智能体',
    title: '让 AI 在明确的权限、资料与工作场景中发挥作用。',
    sub: '面向个人与机构的 AI 工作空间，以上下文、权限与人工审核为基础构建。',
    partner: '与我们合作', explore: '探索产品',
    facts: [['3', '款 AI 产品'], ['4 阶段', '合作流程'], ['人工', '交付前审核']],
    stage: '概念示意：需求、计划、资料来源、任务与角色、人工审核',
    legend: ['输入', '协调', '人工审核'],
    filmLabel: '认识 AgentecFusion', filmTitle: <>从 AI 研究，<br />到真实可用的产品。</>,
    filmNote: '简短了解我们是谁、在做什么，以及如何与机构合作。',
    filmName: 'AgentecFusion · 品牌影片', filmHint: '点击后才加载', play: '播放品牌影片（点击后才加载）',
    filmPoster: '品牌影片封面', filmLoading: '正在加载视频播放器…', filmError: '视频暂时无法加载，请使用备用链接观看。',
    backup: '在备用链接观看', transcript: '阅读文字稿', filmSoon: '品牌影片即将上线',
    flowPill: '工作流示意', flowTitle: '从一个需求，到一次人工决策。',
    flowNote: '概念示意，不连接后台，也不执行真实任务。', flowSteps: '工作流步骤', step: '步骤',
    steps: [
      ['输入', 't-in', '从需求与上下文出发', '明确任务、可用资料与权限边界。'],
      ['协调', 't-co', '把角色与任务组织起来', '展示计划、调研与执行之间的关系。'],
      ['审核', 't-re', '让人留在决策节点', '核对证据、审阅成果，并决定下一步。']
    ],
    solEyebrow: '定制方案', solTitle: <>围绕您的机构<br />量身构建。</>,
    solSub: '围绕具体场景、现有系统与数据边界，探讨适合您所在机构的 AI 方案。',
    solButton: '了解定制服务', principles: ['与您共同界定范围', '数据始终在边界内', '每一步都有人工审核'],
    solRows: [
      ['明确需求', '场景与目标', '我们从一个具体的工作流开始——谁在使用、希望改变什么、如何判断成功。', ['负责人', '使用者', '成功标准']],
      ['评估适配', '系统与边界', '梳理涉及的系统、数据与权限，并约定智能体可以触及与不可触及的范围。', ['系统', '数据', '权限']],
      ['规划验证', '审核与验收', '在上线之前，明确结果如何审核、由谁签字确认，以及怎样才算验收通过。', ['审核人', '标准', '签字确认']]
    ],
    partEyebrow: '合作', partTitle: '让好的想法走得更远。', partLink: '探索合作方式',
    paths: [
      ['research', '学术研究', '高校与研究机构', '联合研究、在教学场景中试点 Learn，以及学生项目。', '以学术伙伴身份咨询'],
      ['industry', '产品合作', '企业与机构', '基于您自己的数据共同设计智能体工作流，并设置清晰的审核节点。', '以产业伙伴身份咨询'],
      ['investment', '战略与投资', '战略投资者', '交流 AgentecFusion 的发展方向，以及如何共同成长。', '咨询投资合作']
    ],
    ctaTitle: <>有值得一起<br />解决的问题吗？</>,
    ctaText: '告诉我们您所在的机构和正在探索的方向，我们会转给合适的同事跟进。',
    ctaTalk: '与团队交流', ctaExplore: '探索产品'
  }
};

const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

// Hero connection flow: plays once per visit, after the hero copy has appeared.
function useHeroFlow(stage) {
  useEffect(() => {
    const svg = stage.current?.querySelector('svg.iso');
    if (!svg) return undefined;
    const NS = 'http://www.w3.org/2000/svg';
    const group = svg.querySelector('#lines');
    const d = (id) => svg.querySelector('#' + id).getAttribute('d');
    const routes = { N: d('dash-n'), A: d('grey-b'), B: d('grey-c'), C: d('grey-a') };
    // All links start neutral; the static blue Need→Plan dash becomes a grey base.
    const glowN = svg.querySelector('#glow-n');
    glowN.setAttribute('stroke', '#7A8394'); glowN.setAttribute('stroke-opacity', '.2');
    const dashN = svg.querySelector('#dash-n');
    dashN.setAttribute('stroke', '#7F7F7F'); dashN.setAttribute('stroke-width', '1.5'); dashN.removeAttribute('stroke-dasharray');
    const created = [];
    const path = (pathData, width, opacity) => {
      const p = document.createElementNS(NS, 'path');
      Object.entries({ d: pathData, fill: 'none', stroke: '#5146E5', 'stroke-width': width, 'stroke-opacity': opacity, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }).forEach(([k, v]) => p.setAttribute(k, v));
      group.appendChild(p); created.push(p); return p;
    };
    const R = {};
    for (const key of Object.keys(routes)) {
      const glow = path(routes[key], 9, 0.18);
      const core = path(routes[key], 2.6, 1);
      const length = core.getTotalLength();
      [glow, core].forEach((p) => { p.style.strokeDasharray = length; p.style.strokeDashoffset = length; });
      const head = document.createElementNS(NS, 'circle');
      Object.entries({ r: '4.2', fill: '#fff', stroke: '#5146E5', 'stroke-width': '2', class: 'flow-head' }).forEach(([k, v]) => head.setAttribute(k, v));
      head.style.opacity = 0;
      group.appendChild(head); created.push(head);
      R[key] = { glow, core, head, length };
    }
    const nearest = (key, x, y) => {
      const r = R[key]; let best = 0; let bestDistance = Infinity;
      for (let s = 0; s <= r.length; s += 2) { const p = r.core.getPointAtLength(s); const dist = (p.x - x) ** 2 + (p.y - y) ** 2; if (dist < bestDistance) { bestDistance = dist; best = s; } }
      return best;
    };
    const sTasks = nearest('B', 430, 494);
    const sSources = nearest('A', 430, 128);
    const set = (key, len) => {
      const r = R[key]; const offset = r.length - len;
      r.glow.style.strokeDashoffset = offset; r.core.style.strokeDashoffset = offset;
      const p = r.core.getPointAtLength(Math.max(0, len));
      r.head.setAttribute('cx', p.x); r.head.setAttribute('cy', p.y);
    };
    // Phases: [start, duration, [route, from, to]…]
    const phases = [
      [0, 1100, [['N', 0, R.N.length], ['B', 0, sTasks]]],
      [1100, 1000, [['A', 0, sSources], ['B', sTasks, R.B.length]]],
      [2100, 900, [['A', sSources, R.A.length]]],
      [3000, 1300, [['C', 0, R.C.length]]]
    ];
    const total = 4300;
    let frame; let timer;
    if (reducedMotion()) Object.keys(R).forEach((key) => set(key, R[key].length));
    else {
      let start = null;
      const tick = (now) => {
        if (start === null) start = now;
        const elapsed = now - start;
        for (const [begin, duration, list] of phases) {
          const u = Math.min(1, Math.max(0, (elapsed - begin) / duration));
          const active = u > 0 && u < 1;
          for (const [key, from, to] of list) { if (elapsed >= begin) set(key, from + (to - from) * ease(u)); R[key].head.style.opacity = active ? 1 : 0; }
        }
        if (elapsed < total) frame = requestAnimationFrame(tick);
        else Object.values(R).forEach((r) => { r.head.style.opacity = 0; });
      };
      timer = window.setTimeout(() => { frame = requestAnimationFrame(tick); }, 450);
    }
    return () => { window.clearTimeout(timer); cancelAnimationFrame(frame); created.forEach((node) => node.remove()); };
  }, [stage]);
}

function Hero({ t }) {
  const stage = useRef(null);
  useHeroFlow(stage);
  return <section className="hero" aria-labelledby="h1">
    <div className="wrap">
      <div className="hero-copy">
        <p className="proof" style={{ '--i': 0 }}>{t.proof}</p>
        <h1 id="h1" style={{ '--i': 1 }}>{t.title}</h1>
        <p className="sub" style={{ '--i': 2 }}>{t.sub}</p>
        <div className="ctas" style={{ '--i': 3 }}>
          <FadeLink className="btn btn-light" to="/partnerships">{t.partner}</FadeLink>
          <FadeLink className="btn btn-dark" to="/products">{t.explore} <span className="ar" aria-hidden="true">→</span></FadeLink>
        </div>
        <div className="facts" style={{ '--i': 4 }}>
          {t.facts.map(([value, label], index) => <React.Fragment key={index}>{index > 0 && <div className="div" />}<div className="f"><strong>{value}</strong><span>{label}</span></div></React.Fragment>)}
        </div>
      </div>
      <div className="stage" ref={stage} role="img" aria-label={t.stage}>
        <div className="wash" aria-hidden="true" />
        <Markup as="div" className="iso-host" markup={heroIso} />
        <div className="legend" aria-hidden="true">
          <span className="l"><i>01</i>{t.legend[0]}</span><span className="sep" />
          <span className="l"><i>02</i>{t.legend[1]}</span><span className="sep" />
          <span className="l hot"><i>03</i>{t.legend[2]}</span>
        </div>
      </div>
    </div>
  </section>;
}

function Film({ t, language }) {
  const { film, loading } = useSiteContent();
  const [state, setState] = useState('idle');
  const configured = Boolean(film?.src && film?.fallbackUrl);
  const text = (value) => value && (value[language] || value.en || value.zh);
  const transcript = text(film?.transcript);
  useEffect(() => {
    if (state !== 'loading' || film?.type === 'file') return undefined;
    const timeout = window.setTimeout(() => setState('error'), 12000);
    return () => window.clearTimeout(timeout);
  }, [state, film]);
  const playing = state === 'loading' || state === 'ready';
  const src = film?.type === 'bilibili' ? film.src.replace('autoplay=0', 'autoplay=1') : film?.src;
  return <Section className="film" {...HOME_SECTION}>
    <div className="wrap">
      <div className="sec-head" data-reveal="">
        <div><p className="label">{t.filmLabel}</p><h2>{t.filmTitle}</h2></div>
        <p className="note">{t.filmNote}</p>
      </div>
      <div className={cx('poster', playing && 'playing')} data-reveal="" style={{ '--i': 1 }} aria-label={t.filmPoster} role="group">
        {playing && (film.type === 'file'
          ? <video className="player" src={film.src} controls autoPlay playsInline poster={film.poster} aria-label={text(film.title) || t.filmName} onLoadedData={() => setState('ready')} onError={() => setState('error')} />
          : <iframe className="player" src={src} title={text(film.title) || t.filmName} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" onLoad={() => setState('ready')} />)}
        {!playing && <>
          {configured && <button type="button" className="play" aria-label={t.play} onClick={() => setState('loading')}><svg width="45" height="51" viewBox="0 0 45 51" aria-hidden="true"><path d="M6 4.5v42L42 25.5z" fill="#fff" stroke="#fff" strokeWidth="4" strokeLinejoin="round" /></svg></button>}
          <div className="cap">
            <div><b>{t.filmName}</b><small role="status">{state === 'error' ? t.filmError : configured ? (text(film.title) ? `${text(film.title)} · ${t.filmHint}` : t.filmHint) : loading ? '' : t.filmSoon}</small></div>
            {configured && <nav>
              {transcript && <details className="transcript"><summary>{t.transcript}</summary><p>{transcript}</p></details>}
              <a href={film.fallbackUrl} target="_blank" rel="noopener noreferrer">{t.backup} ↗</a>
            </nav>}
          </div>
        </>}
        {state === 'loading' && <p className="film-status" role="status">{t.filmLoading}</p>}
      </div>
    </div>
  </Section>;
}

function Workflow({ t }) {
  const section = useRef(null);
  const cols = useRef([]);
  const [active, setActive] = useState(0);
  const [token, setToken] = useState(0);
  const activate = (index) => { setActive(index); setToken((n) => n + 1); };
  // The design adds a highlight row behind the first list item of card 1.
  useEffect(() => {
    const root = section.current;
    const list = root?.querySelector('#wf1-list');
    const row = root?.querySelector('#wf1-row-0');
    if (!list || !row || root.querySelector('#wf1-hl')) return undefined;
    const highlight = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    Object.entries({ id: 'wf1-hl', x: 121, y: 89, width: 291, height: 46, rx: 10, fill: '#EEEDFD', stroke: '#5146E5', 'stroke-opacity': 0.25 }).forEach(([k, v]) => highlight.setAttribute(k, v));
    list.insertBefore(highlight, row);
    return () => highlight.remove();
  }, []);
  // Restart the active column's progress and card micro-animation on every activation.
  useLayoutEffect(() => {
    if (!token) return;
    cols.current.forEach((col) => col?.classList.remove('play'));
    const col = cols.current[active];
    if (!col) return;
    void col.offsetWidth;
    col.classList.add('play');
  }, [token, active]);
  const visuals = [workflow1, workflow2, workflow3];
  return <Section className="flow" id="workflow" {...HOME_SECTION} onEnter={() => setToken((n) => n || 1)}>
    <div className="wrap" ref={section}>
      <div className="center-head" data-reveal="">
        <span className="pill">{t.flowPill}</span>
        <h2>{t.flowTitle}</h2>
        <p>{t.flowNote}</p>
      </div>
      <div className="steps3" role="tablist" aria-label={t.flowSteps}>
        {t.steps.map(([tag, tagClass, title, text], index) => (
          <div key={index} ref={(node) => { cols.current[index] = node; }} className={cx('col', `c${index + 1}`, index === active && 'active', index < active && 'done')}
            role="tab" tabIndex={0} aria-selected={index === active}
            onMouseEnter={() => activate(index)} onClick={() => activate(index)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); activate(index); }
              if (event.key === 'ArrowRight') cols.current[Math.min(2, index + 1)]?.focus();
              if (event.key === 'ArrowLeft') cols.current[Math.max(0, index - 1)]?.focus();
            }}>
            <div className="track"><span className="fill" /><span className="knob" /></div>
            <div className="col-meta"><span className="l"><span className="num">{`0${index + 1}`}</span><span className={cx('tag', tagClass)}>{tag}</span></span><span className="arrow" aria-hidden="true">→</span></div>
            <h3>{title}</h3>
            <p>{text}</p>
            <div className="wcard"><span className="lb"><i />{`${t.step} 0${index + 1}`}</span><Markup as="div" className="vis" markup={visuals[index]} /></div>
          </div>
        ))}
      </div>
    </div>
  </Section>;
}

function SolutionsTeaser({ t }) {
  const STEP = 5000;
  const rows = t.solRows;
  const [current, setCurrent] = useState(0);
  const [timed, setTimed] = useState(false);
  const [token, setToken] = useState(0);
  const [paused, setPaused] = useState(false);
  const show = (index, timing) => {
    setCurrent(index);
    setTimed(Boolean(timing) && index < rows.length - 1 && !reducedMotion());
    setToken((n) => n + 1);
  };
  return <Section className="sol" id="solutions" {...HOME_SECTION} onEnter={() => show(0, true)}>
    <div className="wrap">
      <div data-reveal="">
        <p className="eyebrow">{t.solEyebrow}</p>
        <h2>{t.solTitle}</h2>
        <p className="sub">{t.solSub}</p>
        <FadeLink className="btn btn-dark" to="/solutions">{t.solButton} <span className="ar" aria-hidden="true">↗</span></FadeLink>
        <div className="principles">{t.principles.map((item) => <span key={item}>{item}</span>)}</div>
      </div>
      <ol className={cx('slist', paused && 'paused')} data-reveal="" style={{ '--i': 2, '--step-ms': `${STEP}ms` }} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
        {rows.map(([title, subtitle, text, chips], index) => (
          <li key={index} className={cx('srow', index === current && 'on', index < current && 'past', index === current && timed && 'timing')}>
            <div className="mark" aria-hidden="true"><span className="dot" /><span className="seg"><b key={index === current ? token : 'idle'} onAnimationEnd={() => { if (index === current && timed) show(index + 1, true); }} /></span></div>
            <div className="sbox" role="button" tabIndex={0} aria-expanded={index === current}
              onClick={() => show(index, true)}
              onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); show(index, true); } }}>
              <div className="top"><span className="n">{`0${index + 1}`}</span><div><h3>{title}</h3><p className="s">{subtitle}</p></div><span className="go" aria-hidden="true">↗</span></div>
              <div className="sbody"><div><p>{text}</p>
                <div className="chips">{chips.map((chip) => <span key={chip}>{chip}</span>)}</div>
                <div className="bars">{[0, 1, 2].map((bar) => <i key={bar} className={bar <= index ? 'f' : undefined} />)}</div></div></div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  </Section>;
}

function Partnerships({ t }) {
  const visuals = [partnerAcademic, partnerIndustry, partnerInvestment];
  return <Section className="partners" id="partnerships" {...HOME_SECTION}>
    <div className="wrap">
      <div className="sec-head" data-reveal="">
        <div><p className="eyebrow">{t.partEyebrow}</p><h2>{t.partTitle}</h2></div>
        <FadeLink className="link" to="/partnerships">{t.partLink} <span className="ar" aria-hidden="true">↗</span></FadeLink>
      </div>
      <div className="paths">
        {t.paths.map(([type, kicker, title, text, action], index) => (
          <FadeLink key={type} className="pathc" to={`/contact?type=${type}`} data-reveal="" style={{ '--i': index + 1 }}>
            <span className="h">{kicker}<span>{`0${index + 1}`}</span></span>
            <Markup className="v" markup={visuals[index]} />
            <h3>{title}</h3><p>{text}</p>
            <span className="f">{action}<i aria-hidden="true">↗</i></span>
          </FadeLink>
        ))}
      </div>
    </div>
  </Section>;
}

function ClosingCta({ t }) {
  return <Section className="cta" id="contact" {...HOME_SECTION}>
    <div className="wrap">
      <div className="cpanel" data-reveal="">
        <div><h2>{t.ctaTitle}</h2><p>{t.ctaText}</p></div>
        <div className="btns">
          <FadeLink className="btn btn-white" to="/contact">{t.ctaTalk} <span className="ar" aria-hidden="true">→</span></FadeLink>
          <FadeLink className="btn btn-outline" to="/products">{t.ctaExplore}</FadeLink>
        </div>
      </div>
    </div>
  </Section>;
}

export default function HomePage() {
  const { t, language } = useCopy(copy);
  return <>
    <Hero t={t} />
    <Film t={t} language={language} />
    <Workflow t={t} />
    <ProductFamily homeThreshold />
    <SolutionsTeaser t={t} />
    <Partnerships t={t} />
    <ClosingCta t={t} />
  </>;
}
